import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Block, BlockContent, LinkBlockContent } from '../types';
import { useEditorStore } from '../editor-store';
import { NeueBasicTextEditor } from '../components/neue-basic-text-editor';
import { Button } from 'src/common/button';
import { useElementDimensionChange } from 'src/utils/element/dimension-change.hook';
import { DestinationLogo } from 'src/common/DestinationLogo';
import { FullscreenPreviewModal } from 'src/common/FullscreenPreviewModal';
import { CloseStrongIcon, ShareLinkOutStrongIcon } from 'src/monet/icons';
import { IconButton } from 'src/common/icon-button';
import { LinkInlineEmbed } from './inline-embed';

type Props = {
  block: Block;
  selected: boolean;
  editable: boolean;
  useActiveStyle: boolean;
  forceHoverState?: boolean;
  contextMenuOpen?: boolean;
  onBlockSizeChange?: (width: number, height: number) => void;
  onUpdateLinkTitle?:  (title: string) => void;
  onUpdateLinkCta?: (cta: string) => void;
};

export const LinkBlock = ({ 
  block, selected, editable, 
  useActiveStyle, forceHoverState = false, contextMenuOpen = false ,
  onBlockSizeChange, 
  onUpdateLinkTitle, 
  onUpdateLinkCta
}: Props) => {
  const blockContent : LinkBlockContent | undefined = block.content.type === 'link' ? block.content : undefined;
  const selectBlock = useEditorStore((state) => state.selectBlock);
  const linkContentRef = useRef<HTMLDivElement>(null);
  const blockRef = useRef<HTMLDivElement>(null);
  
  const modalOpenRef = useRef<() => void>();
  const modalCloseRef = useRef<() => void>();

  const { dimensions } = useElementDimensionChange(blockRef);
  const isMobile = dimensions?.clientWidth && dimensions?.clientWidth < 768;

  const onModalOpen = () => {
    if (modalOpenRef.current) {
      modalOpenRef.current();
    }
  }

  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      if (linkContentRef.current) {
        const { width, height } = linkContentRef.current.getBoundingClientRect();
        if (blockContent?.displayOption === 'new-tab') {
          onBlockSizeChange && onBlockSizeChange(width, height);
        }
      }
    });

    if (linkContentRef.current) {
      ro.observe(linkContentRef.current);
    }
    return () => {
      ro.disconnect();
    };
  }, [dimensions, linkContentRef, blockContent?.displayOption]);

  if (!blockContent?.link) return null;

  const { link, displayOption, hideImage, openInNewTab, cta } = blockContent;

  const canLinkEmbed = link?.iframe_safe || false;
  const hasOpenInNewTabValue = typeof openInNewTab !== 'undefined';
  const openInNewTabValue = canLinkEmbed ? (hasOpenInNewTabValue ? openInNewTab : false) : true;    

  const isInline = displayOption !== 'new-tab';

  const renderLinkMobile = (link: LinkBlockContent['link']) => {
    return (
      <div className="flex flex-col space-y-4 w-full rounded-2xl">
        {link.thumbnail && link.thumbnail.url && !hideImage && (
          <img
            alt={link.title}
            className="rounded-lg max-h-[248px] max-w-full transition-all self-start"
            src={link.thumbnail.url} style={{
              ...(link.thumbnail.width && link.thumbnail.height && {
                aspectRatio: `${link.thumbnail.width} / ${link.thumbnail.height}`,
                ...(link.thumbnail.width < 248 && {
                    maxWidth: `${link.thumbnail.width}px`,
                }),
              }),
            }} />
        )}
        <div className="flex flex-1 flex-col space-y-4">
          {renderTitle(link)}
          {renderButton(link)}
        </div>
      </div>
    )
  }

  const renderTitle = (link: LinkBlockContent['link']) => {
    return (
      <div className="flex flex-1 flex-col max-w-full">
        <NeueBasicTextEditor
          content={link.title || ''}
          editable={editable}
          selected={selected}
          autoSelectWhenSelected={true}
          textTypes={['heading']}
          textPlaceholder='Link title'
          preventEnterPress={true}
          canReceiveUpdates={true}
          onFocus={() => { }}
          onUpdate={(content: string, textContent: string) => {
            onUpdateLinkTitle && onUpdateLinkTitle(textContent);
          }} />
        <a href={ link.url || link.transformed_url } target="_blank" rel="noopener noreferrer" style={{
          textDecoration: 'none',
        }}>
          <div className="flex space-x-2 items-center">
            <DestinationLogo externalLinkUrl={link.url || link.transformed_url} size='xsmall' />
            <div className="text-bedrock-p text-bedrock-black/40 truncate">
              {link.url || link.transformed_url}
            </div>
          </div>
        </a>
      </div>
    )
  }

  const renderButton = (link: LinkBlockContent['link']) => {
    if (editable) {
      return (
        <div className="flex justify-start">
          <div className="btn-bedrock-primary hover:opacity-80 hover:cursor-text">
            <NeueBasicTextEditor
              content={cta || 'Open link'}
              editable={editable}
              selected={selected}
              textTypes={['paragraph']}
              preventEnterPress={true}
              canReceiveUpdates={true}
              textClasses={{ paragraph: classNames('!text-bedrock-p-strong !text-white') }}
              onUpdate={(content: string, textContent: string) => {
                onUpdateLinkCta && onUpdateLinkCta(textContent);
              }} />
          </div>
        </div>        
      )
    } else {
      return (
        <a href={link.url || link.transformed_url} target="_blank" rel="noopener noreferrer">
          <div className="flex flex-shrink-0 justify-self-end">
            <Button
              type='button'
              size='regular'
              variant='primary'
              label={cta || 'Open link'}
              onClick={(e) => {
                console.log("open new tab", openInNewTabValue);
                if (!openInNewTabValue) {
                  e && e.preventDefault();
                  e && e.stopPropagation();
                  onModalOpen();
                }
              }}
            />
          </div>
        </a>
      )
    }    
  } 

  const renderLinkDesktop = (link: LinkBlockContent['link']) => {
    return (
      <div className="flex space-x-4 items-center rounded-2xl">
        {link.thumbnail && link.thumbnail.url && !hideImage && (
          <img
            alt={link.title}
            className="rounded-lg max-h-[70px] max-w-[130px] transition-all"
            src={link.thumbnail.url} style={{
              ...(link.thumbnail.width && link.thumbnail.height && {
                aspectRatio: `${link.thumbnail.width} / ${link.thumbnail.height}`,
              }),
            }} />
        )}
        {renderTitle(link)}
        {renderButton(link)}
      </div>
    )
  }

  const renderLink = (link: LinkBlockContent['link']) => {
    return (
      <div className={classNames('w-full h-full bg-transparent relative neue-link-block', {
        'flex items-center': !isInline
      })}>
        {isInline ? (
          <LinkInlineEmbed link={link} />
        ): (
          <div ref={linkContentRef} className="w-full p-4">
            {isMobile ? renderLinkMobile(link) : renderLinkDesktop(link)}
          </div>
        )}
        <FullscreenPreviewModal
          openRef={modalOpenRef}
          //@ts-ignore
          closeRef={modalCloseRef}
          dialogClassName="z-neue-lightbox my-4 mx-9 md:mx-20"
          overlayClassName="bg-bedrock-black/80"
          renderClose={(closeFn) => (
            <div className='flex fixed z-10 top-4 md:top-8 right-2 md:right-8'>
              <div className="flex flex-col space-y-4">
                <IconButton onClick={closeFn}>
                  <CloseStrongIcon className='w-5 h-5 text-white' />
                </IconButton>
                <a href={link.url || link.transformed_url} target="_blank" rel="noopener noreferrer">
                  <IconButton>
                    <ShareLinkOutStrongIcon className='w-5 h-5 text-white' />
                  </IconButton>
                </a>                
              </div>              
            </div>
          )}>
          <LinkInlineEmbed link={link} />
        </FullscreenPreviewModal>
      </div>
    )
  }

  return (
    <div 
      ref={blockRef}
      className={classNames('group/linkblock w-full h-full relative transition rounded-2xl bg-transparent', {
      'bg-bedrock-black/[.03]': !isInline,
      'hover:bg-white bg-white': selected,
    })}>
      <div className={classNames('absolute transition-all inset-0 rounded-2xl z-10')}>
        {renderLink(link)}
      </div>
      <div
        onClick={(e) => {
          if (!selected) {
            e.preventDefault();
            e.stopPropagation();
            selectBlock(block.id);
          }
        }}
        className={classNames('absolute inset-0 rounded-2xl transition bg-transparent z-20', {
          'hidden': !editable || selected,
          'pointer-events-none': selected,
        })}
      ></div>
    </div>    
  )
};
